<template>
  <div class="not-found-page">
    <!-- Carousel Hero Section -->
    <section class="banner__two slider-two position-relative">
      <div class="pattern-layer">
        <div class="pattern-1 position-absolute" data-parallax='{"x": 100}' style="background-image: url('/shapes/shape-06.png')"></div>
      </div>
      <div class="image-layer position-absolute" style="background-image: url('/banner/banner-two.jpg')"></div>
      <div class="banner__data__two position-relative">
        <div class="banner-carousel owl-theme owl-carousel owl-dots-none">
          <div class="slide-item position-relative">
            <div class="container">
              <div class="row">
                <div class="col-lg-6 col-md-12">
                  <div class="content-box position-relative">
                    <div class="sub__title">
                      <h4 class="position-relative">
                        <span class="slider-text-anim two">Page Not Found</span>
                      </h4>
                    </div>
                    <h2 class="position-relative">
                      <span class="slider-text-anim">Oops! The page </span><br />
                      <span class="slider-text-anim title__last">"<span>{{ pageName || 'Unknown' }}</span>" was not found</span>
                    </h2>
                    <p>{{ getRouteDescription() }}</p>
                    <div class="btn-box">
                      <router-link to="/" class="theme-btn theme-btn-one">
                        <i class="icon-02"></i> Go Home
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="slide-item position-relative">
            <div class="container">
              <div class="row">
                <div class="col-lg-6 col-md-12">
                  <div class="content-box position-relative">
                    <div class="sub__title">
                      <h4 class="position-relative">
                        <span class="slider-text-anim two">Let's Help You</span>
                      </h4>
                    </div>
                    <h2 class="position-relative">
                      <span class="slider-text-anim title__last">Find What You're <span>Looking For</span></span>
                    </h2>
                    <p>Explore our main sections or get in touch with our team to find the right solution for your needs.</p>
                    <div class="btn-box">
                      <router-link to="/synergy" class="theme-btn theme-btn-one">
                        <i class="icon-02"></i> View Services
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="slide-item position-relative">
            <div class="container">
              <div class="row">
                <div class="col-lg-6 col-md-12">
                  <div class="content-box position-relative">
                    <div class="sub__title">
                      <h4 class="position-relative">
                        <span class="slider-text-anim two">Sogdo Solutions</span>
                      </h4>
                    </div>
                    <h2 class="position-relative">
                      <span class="slider-text-anim title__last">Explore Our <span>Main Sections</span></span>
                    </h2>
                    <p>Discover our comprehensive range of software solutions, research initiatives, and technology services.</p>
                    <div class="btn-box">
                      <router-link to="/contact" class="theme-btn theme-btn-one">
                        <i class="icon-02"></i> Contact Us
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="banner__bottom">
          <div class="row">
            <div class="col-lg-6 col-md-12"></div>
            <div class="col-lg-6 col-md-12">
              <div class="banner__mission__block">
                <div class="mission__block">
                  <h3>Our Vision</h3>
                  <p>To be the most trusted software innovation partner, building meaningful technology for real-world challenges.</p>
                </div>
                <div class="mission__block">
                  <h3>Our Mission</h3>
                  <p>To build meaningful technology for real-world challenges through cutting-edge research, innovative development, and breakthrough discoveries that transform industries.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Related Content Section -->
    <section class="related-content-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-8 col-md-12">
            <div class="content-box">
              <h3>What you might be looking for:</h3>
              <div class="suggestions">
                <div v-for="suggestion in getSuggestions()" :key="suggestion.title" class="suggestion-item">
                  <h4>{{ suggestion.title }}</h4>
                  <p>{{ suggestion.description }}</p>
                  <router-link :to="suggestion.link" class="theme-btn theme-btn-one">
                    {{ suggestion.buttonText }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-4 col-md-12">
            <div class="content-box">
              <h3>Quick Links</h3>
              <ul class="quick-links">
                <li><router-link to="/">Home</router-link></li>
                <li><router-link to="/synergy">Services</router-link></li>
                <li><router-link to="/research">Research</router-link></li>
                <li><router-link to="/sogdotech">Technology</router-link></li>
                <li><router-link to="/about">About Us</router-link></li>
                <li><router-link to="/contact">Contact</router-link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import WOW from 'wowjs'

export default {
  name: 'NotFound',
  setup() {
    const route = useRoute()

    // Get the attempted route from pathMatch
    const attemptedRoute = computed(() => {
      if (route.params.pathMatch) {
        if (Array.isArray(route.params.pathMatch)) {
          return route.params.pathMatch.join('/')
        }
        return route.params.pathMatch
      }
      return 'Unknown'
    })

    // Extract page name from the attempted route
    const pageName = computed(() => {
      const route = String(attemptedRoute.value)
      if (!route || route === 'Unknown') return 'Unknown'
      
      // Get the last segment of the path
      const segments = route.split('/').filter(segment => segment.length > 0)
      if (segments.length === 0) return 'Unknown'
      
      const lastSegment = segments[segments.length - 1]
      
      // Format the page name (capitalize and replace hyphens/underscores with spaces)
      return lastSegment
        .replace(/[-_]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    })

    // Get route description based on the page name
    const getRouteDescription = () => {
      const name = String(pageName.value).toLowerCase()
      
      if (name.includes('product') || name.includes('service')) {
        return `We offer comprehensive ${name} solutions. Explore our services to find what you're looking for.`
      } else if (name.includes('research') || name.includes('development')) {
        return `Our research and development initiatives span across multiple domains. Discover our latest innovations.`
      } else if (name.includes('technology') || name.includes('tech')) {
        return `Explore our cutting-edge technology solutions and consulting services.`
      } else if (name.includes('contact') || name.includes('support')) {
        return `Get in touch with our team for personalized assistance and support.`
      } else if (name.includes('about') || name.includes('company')) {
        return `Learn more about Sogdo, our mission, vision, and the team behind our innovations.`
      } else {
        return `The page you're looking for doesn't exist, but we can help you find what you need.`
      }
    }

    // Get detailed description
    const getDetailedDescription = () => {
      const name = String(pageName.value).toLowerCase()
      
      if (name.includes('product') || name.includes('service')) {
        return `At Sogdo, we develop world-class software products and services powered by the latest tech stacks. Our comprehensive solutions are designed to be fast, scalable, and smart, helping businesses transform their operations and achieve their goals.`
      } else if (name.includes('research') || name.includes('development')) {
        return `Our research and development initiatives span across diverse domains including AI & Machine Learning, Healthcare Innovation, Blockchain Technologies, and more. We work at the intersection of technology, science, and business to drive meaningful impact.`
      } else if (name.includes('technology') || name.includes('tech')) {
        return `We provide cutting-edge technology consulting and engineering services that adapt to your business needs. From software architecture planning to cloud migration, we help you leverage the latest technologies for growth.`
      } else {
        return `Sogdo is a leading software innovation company that builds meaningful technology for real-world challenges. We specialize in software product development, AI & ML solutions, and comprehensive technology consulting.`
      }
    }

    // Get suggestions based on the page name
    const getSuggestions = () => {
      const name = String(pageName.value).toLowerCase()
      
      if (name.includes('product') || name.includes('service')) {
        return [
          {
            title: 'Our Services',
            description: 'Explore our comprehensive range of software development and consulting services.',
            link: '/synergy',
            buttonText: 'View Services'
          },
          {
            title: 'Technology Solutions',
            description: 'Discover our cutting-edge technology stack and development methodologies.',
            link: '/sogdotech',
            buttonText: 'Learn More'
          }
        ]
      } else if (name.includes('research') || name.includes('development')) {
        return [
          {
            title: 'Research & Development',
            description: 'Explore our innovative research initiatives across multiple domains.',
            link: '/research',
            buttonText: 'View Research'
          },
          {
            title: 'AI & ML Solutions',
            description: 'Discover our artificial intelligence and machine learning capabilities.',
            link: '/ai-ml',
            buttonText: 'Explore AI/ML'
          }
        ]
      } else if (name.includes('technology') || name.includes('tech')) {
        return [
          {
            title: 'Technology Stack',
            description: 'Learn about our modern technology stack and development practices.',
            link: '/sogdotech',
            buttonText: 'View Technology'
          },
          {
            title: 'Our Services',
            description: 'Explore our comprehensive software development and consulting services.',
            link: '/synergy',
            buttonText: 'View Services'
          }
        ]
      } else {
        return [
          {
            title: 'Our Services',
            description: 'Explore our comprehensive range of software development and consulting services.',
            link: '/synergy',
            buttonText: 'View Services'
          },
          {
            title: 'About Sogdo',
            description: 'Learn more about our mission, vision, and the team behind our innovations.',
            link: '/about',
            buttonText: 'About Us'
          },
          {
            title: 'Contact Us',
            description: 'Get in touch with our team for personalized assistance and support.',
            link: '/contact',
            buttonText: 'Contact Us'
          }
        ]
      }
    }

    onMounted(async () => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()

      // Import and initialize Owl Carousel
      try {
        await import('owl.carousel')
        
        // Initialize Owl Carousel for banner
        setTimeout(() => {
          if (window.$ && window.$.fn.owlCarousel) {
            window.$('.banner-carousel').owlCarousel({
              loop: true,
              margin: 0,
              nav: true,
              active: true,
              smartSpeed: 1000,
              autoplay: 6000,
              navText: ['<span class="icon-05"></span>', '<span class="icon-06"></span>'],
              responsive: {
                0: {
                  items: 1
                },
                600: {
                  items: 1
                },
                768: {
                  items: 1
                },
                992: {
                  items: 1
                },
                1200: {
                  items: 1
                }
              }
            })
          }
        }, 100)
      } catch (error) {
        console.error('Error loading Owl Carousel:', error)
      }
    })

    return {
      attemptedRoute,
      pageName,
      getRouteDescription,
      getDetailedDescription,
      getSuggestions
    }
  }
}
</script>

<style scoped>
/* Custom button styling for carousel - More specific to avoid conflicts */
.not-found-page .banner__two .btn-box .theme-btn {
  padding: 12px 30px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  border-radius: 0 !important;
  background: linear-gradient(219deg, #FB0E76 0%, #910694 100%) !important;
  color: #fff !important;
  text-decoration: none !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.3s ease !important;
}

.not-found-page .banner__two .btn-box .theme-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 5px 15px rgba(251, 14, 118, 0.4) !important;
  color: #fff !important;
  text-decoration: none !important;
}

.not-found-page .banner__two .btn-box .theme-btn i {
  font-size: 12px !important;
  transition: transform 0.3s ease !important;
}

.not-found-page .banner__two .btn-box .theme-btn:hover i {
  transform: translateX(3px) !important;
}

/* Mission/Vision block styling */
.banner__mission__block {
  border-radius: 10px 0px 0px 0px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-top: 20px;
}

.mission__block {
  padding: 40px 50px;
  border-right: 1px solid rgba(255, 255, 255, 0.10);
  flex: 1;
}

.mission__block:last-child {
  border-right: none;
}

.mission__block h3 {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.mission__block p {
  color: #ccc;
  font-size: 14px;
  line-height: 24px;
  margin: 0;
  font-family: 'Open Sans', sans-serif;
}

/* Reduce banner height - More specific to avoid conflicts */
.not-found-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

.not-found-page .banner__two .content-box {
  padding-bottom: 60px !important;
}

/* Responsive adjustments - More specific to avoid conflicts */
@media (max-width: 768px) {
  .not-found-page .banner__two .content-box h2 {
    font-size: 50px !important;
    line-height: 60px !important;
  }
  
  .not-found-page .mission__block {
    padding: 30px 25px !important;
  }
  
  .not-found-page .mission__block h3 {
    font-size: 18px !important;
  }
  
  .not-found-page .mission__block p {
    font-size: 13px !important;
    line-height: 22px !important;
  }
}


.related-content-section {
  padding: 80px 0;
  background: #f8f9fa;
  position: relative;
}

.related-content-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.02) 0%, rgba(0, 86, 179, 0.02) 100%);
  z-index: 1;
}

.auto-container {
  position: relative;
  z-index: 2;
}

.content-box h3 {
  color: #1a1a1a;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 40px;
  text-align: center;
  position: relative;
}

.content-box h3::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 2px;
}

.suggestions {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.suggestion-item {
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.suggestion-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.suggestion-item:hover::before {
  transform: scaleY(1);
}

.suggestion-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  border-color: rgba(0, 123, 255, 0.2);
}

.suggestion-item h4 {
  color: #1a1a1a;
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 15px;
  transition: color 0.3s ease;
}

.suggestion-item:hover h4 {
  color: #007bff;
}

.suggestion-item p {
  color: #666;
  margin-bottom: 25px;
  line-height: 1.7;
  font-size: 15px;
}

.suggestion-item .theme-btn {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
  border: none;
  position: relative;
  overflow: hidden;
}

.suggestion-item .theme-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.suggestion-item .theme-btn:hover::before {
  left: 100%;
}

.suggestion-item .theme-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.4);
  color: white;
  text-decoration: none;
}

.quick-links {
  list-style: none;
  padding: 0;
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.quick-links li {
  margin-bottom: 12px;
  position: relative;
  padding-left: 25px;
}

.quick-links li::before {
  content: '→';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  color: #007bff;
  font-weight: bold;
  transition: all 0.3s ease;
  opacity: 0.6;
}

.quick-links li:hover::before {
  transform: translateY(-50%) translateX(5px);
  opacity: 1;
}

.quick-links a {
  color: #666;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: block;
  padding: 8px 0;
  border-radius: 8px;
  padding-left: 10px;
  margin-left: -10px;
}

.quick-links a:hover {
  color: #007bff;
  background: rgba(0, 123, 255, 0.05);
  transform: translateX(5px);
}

@media (max-width: 768px) {
  .related-content-section {
    padding: 60px 0;
  }
  
  .content-box h3 {
    font-size: 24px;
  }
  
  .suggestion-item {
    padding: 20px;
  }
}
</style>
